/**
 * Shared chrome for the run-analysis dialogs (spec D9): pinned header, one
 * scrolling body, sticky footer with Cancel / primary. No Back button: the
 * header dropdown these are launched from (amendment A2) is an anchored
 * popover, which a modal cannot reopen.
 *
 * The dialog keeps its own `<form id={formId}>` (zod schema, mutation,
 * tracker and toast untouched) as the body's child; the primary button sits
 * OUTSIDE it in the footer and submits it through the `form` attribute.
 *
 * Open with `ANALYSIS_SHELL_CLASS` + `ANALYSIS_DIALOG_OPTIONS`.
 */
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useDialog } from '@/providers/DialogProvider';

export interface AnalysisDialogShellProps {
  title: ReactNode;
  description?: ReactNode;
  /** id of the <form> the primary button submits. */
  formId: string;
  primaryLabel: ReactNode;
  primaryDisabled?: boolean;
  primaryBusy?: boolean;
  footerExtra?: ReactNode;
  children: ReactNode;
  // ── Additive to the spec contract (plan D-P3-1) ──
  /** data-testid for the primary button; existing tests address Run by its old id. */
  primaryTestId?: string;
  /**
   * Subscription gate closed: muted + `aria-disabled` + `data-locked`, but
   * still CLICKABLE — the HTML `disabled` attribute would swallow the click
   * that opens the upgrade dialog (CLAUDE.md feature-gating rule).
   */
  primaryLocked?: boolean;
  /** Called INSTEAD of submitting while `primaryLocked` (normally `gate.promptUpgrade`). */
  onPrimaryLockedClick?: () => void;
  /** No primary action at all (an empty state): Cancel is the only button,
   *  rather than a disabled Run that explains nothing. */
  hidePrimary?: boolean;
}

export function AnalysisDialogShell({
  title,
  description,
  formId,
  primaryLabel,
  primaryDisabled,
  primaryBusy,
  footerExtra,
  children,
  primaryTestId,
  primaryLocked,
  onPrimaryLockedClick,
  hidePrimary,
}: AnalysisDialogShellProps) {
  const { t } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();

  // The body keeps a stable scrollbar gutter (no jump when it starts to
  // scroll, no bar over the content). A classic, always-on scrollbar makes
  // that gutter ~15 px wide, which pushed every card that far in from the
  // header and footer edges. The measured gutter is pulled back into the
  // frame's right padding instead, so body, header and footer share one
  // right edge whether the platform's scrollbars are classic (Windows,
  // Linux) or overlay (0 px gutter, macOS).
  const bodyRef = useRef<HTMLDivElement>(null);
  const [gutter, setGutter] = useState(0);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => setGutter(Math.max(0, el.offsetWidth - el.clientWidth));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <>
      {/* `min-w-0` lets the header shrink below a very long layer name;
          `break-words` then wraps it instead of widening the dialog. */}
      <DialogHeader className='min-w-0 shrink-0'>
        <DialogTitle>{title}</DialogTitle>
        {description != null && <DialogDescription className='break-words'>{description}</DialogDescription>}
      </DialogHeader>

      <div
        ref={bodyRef}
        data-slot='analysis-shell-body'
        // `px-1`: room for the cards' focus rings, which the horizontal clip
        // would otherwise cut. The gutter's own width lives in the margin.
        className='min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-1 py-1 [scrollbar-gutter:stable]'
        style={gutter > 0 ? { marginRight: -gutter } : undefined}
      >
        {children}
      </div>

      <DialogFooter
        data-slot='analysis-shell-footer'
        className='shrink-0 border-t border-slate-200/70 pt-4 sm:items-center sm:justify-between dark:border-stone-700/40'
      >
        {/* On a phone the footer stacks as a REVERSED column (DialogFooter's
            `flex-col-reverse`), which put the note under Cancel. `order-last`
            is the top of that column, so it reads above the buttons it
            explains; from `sm` the row order is the DOM's again. */}
        <div className='order-last flex min-w-0 items-center gap-2 sm:order-none'>{footerExtra}</div>
        <div className='flex flex-col-reverse gap-2 sm:flex-row'>
          <Button
            type='button'
            variant='outline'
            data-testid='analysis-shell-cancel'
            disabled={primaryBusy}
            onClick={() => dismissActiveDialog()}
          >
            {t('analysisLauncher.shell.cancel')}
          </Button>
          {!hidePrimary && (
            <Button
              type='submit'
              form={formId}
              data-testid={primaryTestId ?? 'analysis-shell-primary'}
              disabled={primaryDisabled}
              aria-disabled={primaryLocked ? true : undefined}
              data-locked={primaryLocked ? 'true' : undefined}
              className={cn(primaryLocked && 'opacity-60')}
              onClick={(e) => {
                // Locked never submits, with or without a handler — a caller that
                // forgot `onPrimaryLockedClick` must not start a paid run.
                if (primaryLocked) {
                  e.preventDefault();
                  onPrimaryLockedClick?.();
                }
              }}
            >
              {primaryBusy && <Loader2 data-testid='analysis-shell-busy' className='size-4 animate-spin' />}
              {primaryLabel}
            </Button>
          )}
        </div>
      </DialogFooter>
    </>
  );
}
